/**
 * Pure helpers extracted from WorkoutPage.js (no React, no JSX).
 *
 * Behavior MUST stay byte-identical to the originals — these are the live-session
 * primitives (storage, set matching, plate math, set labels) that the live workout
 * page depends on. See docs/superpowers/specs/2026-05-06-workout-page-refactor-design.md.
 */

/**
 * Matches a current set to its corresponding previous set using type-aware matching.
 * - Warmups: takes the LAST N from previous (most relevant warm-up weights)
 * - Work/backoff/others: takes the FIRST N from previous (planned sets; extras ignored)
 * Extra sets that were added mid-session (from_routine=false) are excluded from the count
 * so they don't shift the offset calculation.
 * Falls back to position-match for old sessions without set_type data.
 */
export function getMatchingPreviousSet(previousWeights, currentSets, targetSetIndex) {
  if (!previousWeights || previousWeights.length === 0) return null;

  const targetType = currentSets[targetSetIndex]?.set_type || 'work';
  const hasPrevTypes = previousWeights.some(w => w.set_type);

  // Fallback: no type data in previous session → use position
  if (!hasPrevTypes) return previousWeights[targetSetIndex] || null;

  // Group previous sets by type
  const prevByType = {};
  previousWeights.forEach(w => {
    const t = w.set_type || 'work';
    if (!prevByType[t]) prevByType[t] = [];
    prevByType[t].push(w);
  });

  // Count total ROUTINE sets of this type (exclude mid-session additions to avoid shifting the offset)
  const totalCurrentOfType = currentSets.filter(s => s.from_routine !== false && (s.set_type || 'work') === targetType).length;

  // Relative index of this set within its type group (0-based), counting only routine sets
  const relativeIndex = currentSets
    .slice(0, targetSetIndex + 1)
    .filter(s => s.from_routine !== false && (s.set_type || 'work') === targetType).length - 1;

  const prevSetsOfType = prevByType[targetType] || [];

  if (targetType === 'warmup') {
    // Take the LAST N warmups — skip extras added at the start of a warmup ramp.
    // If current has MORE warmups than previous, clamp to the last available
    // so extra warmups still show the previous warmup weight instead of "-".
    const offset = Math.max(0, prevSetsOfType.length - totalCurrentOfType);
    const idx = Math.min(offset + relativeIndex, prevSetsOfType.length - 1);
    return prevSetsOfType[idx] || null;
  } else {
    // Take the FIRST N — extras appended at the end are ignored.
    // If current has more sets than previous, clamp to the last available
    // so extra sets still show the previous weight instead of "-".
    const idx = Math.min(relativeIndex, prevSetsOfType.length - 1);
    return prevSetsOfType[idx] || null;
  }
}

export const BODYWEIGHT_EQUIPMENT = new Set(['Body Weight', 'Assisted']);
// Lowercase synonyms — covers DB casing variants ("body weight", "bodyweight"),
// custom-created exercises with blank/None equipment, and Hevy-imported entries
// that came in as "none" / "n/a", plus non-load gear (bands, balls, rope,
// rollers, tire) where reps without weight is the normal logging shape.
const BODYWEIGHT_EQUIPMENT_NORMALIZED = new Set([
  'body weight', 'bodyweight', 'body-weight', 'assisted', 'none', 'n/a', '',
  'band', 'resistance band', 'stability ball', 'bosu ball',
  'rope', 'roller', 'wheel roller',
]);
export const isBodyweightExercise = (equipment) =>
  !equipment || BODYWEIGHT_EQUIPMENT.has(equipment) ||
  BODYWEIGHT_EQUIPMENT_NORMALIZED.has(String(equipment).trim().toLowerCase());

// Equipment where very heavy loads are realistic (leg press, sled, Smith-loaded squats, etc.).
// Values match the taxonomy used in the DB — both the ExerciseDB import ("Leverage Machine",
// "Sled Machine", "Smith Machine") and the legacy seed ("Machine"). Cable curls are tagged
// "Cable" not "Machine" so cable exercises are NOT exempt from the flat 500kg threshold.
export const MACHINE_EQUIPMENT = new Set([
  'Leverage Machine',
  'Sled Machine',
  'Smith Machine',
  'Machine',
]);
export const isMachineExercise = (equipment) => !!equipment && MACHINE_EQUIPMENT.has(equipment);

// Unrealistic-value thresholds. Returns { repsLimit, weightLimit } given exercise
// equipment + user history for that exercise.
//
//   historyStats = { session_count, max_weight, max_reps } — zeros = no history.
//
// With 10+ prior sessions: trust the user's own history (2x their all-time max,
// with a floor of 30 on reps so an 8→20-rep PR doesn't nag).
// With <10 sessions: flat ceilings, higher for machine equipment.
export const getUnrealisticLimits = (equipment, historyStats) => {
  const sessions = historyStats?.session_count || 0;
  if (sessions >= 10) {
    const maxReps = historyStats.max_reps || 0;
    const maxWeight = historyStats.max_weight || 0;
    return {
      repsLimit: Math.max(30, maxReps * 2),
      // Guard against a zero max_weight (bodyweight-only history) — fall back
      // to the flat ceiling so we don't cap everything at 0.
      weightLimit: maxWeight > 0 ? maxWeight * 2 : (isMachineExercise(equipment) ? 1000 : 500),
    };
  }
  return {
    repsLimit: 100,
    weightLimit: isMachineExercise(equipment) ? 1000 : 500,
  };
};

export const computeUnrealisticFlags = (reps, weight, equipment, historyStats) => {
  const { repsLimit, weightLimit } = getUnrealisticLimits(equipment, historyStats);
  return {
    reps: (reps || 0) > repsLimit,
    weight: (weight || 0) > weightLimit,
  };
};

/**
 * Smart set label: work sets get sequential numbers (1,2,3…),
 * failure sets get workNumber+F (e.g. "3F"), others get a letter (W, D, B).
 */
export function getSetDisplayLabel(sets, setIdx) {
  const set = sets[setIdx];
  const type = set.set_type || 'work';
  if (type === 'warmup') return 'W';
  if (type === 'drop') return 'D';
  if (type === 'backoff') return 'B';
  // Count work + failure sets up to this index (failure continues the work count)
  let workNum = 0;
  for (let i = 0; i <= setIdx; i++) {
    const t = sets[i].set_type || 'work';
    if (t === 'work' || t === 'failure') workNum++;
  }
  if (type === 'failure') return `${workNum}F`;
  return String(workNum); // work set
}

/* ── Plate Calculator ────────────────────────────────────────── */
export const PLATE_SETS = [25, 20, 15, 10, 5, 2.5, 1.25];
export const PLATE_COLORS = { 25: '#ef4444', 20: '#3b82f6', 15: '#eab308', 10: '#22c55e', 5: '#f8fafc', 2.5: '#f8fafc', 1.25: '#f8fafc' };

export function calculatePlates(totalWeight, barWeight = 20) {
  const perSide = (totalWeight - barWeight) / 2;
  if (perSide < 0) return { plates: [], perSide: 0, barWeight };
  if (perSide === 0) return { plates: [], perSide: 0, barWeight };
  let remaining = perSide;
  const plates = [];
  for (const plate of PLATE_SETS) {
    while (remaining >= plate) {
      plates.push(plate);
      remaining -= plate;
    }
  }
  return { plates, perSide, barWeight, remainder: Math.round(remaining * 100) / 100 };
}

/* ── LocalStorage helpers ─────────────────────────────────────── */
export const STORAGE_KEY = 'trainedbyrs_active_workout';

export function createWorkoutExerciseId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `live-ex-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createSetKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `live-set-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function ensureWorkoutExerciseIds(exercises = []) {
  return exercises.map((exercise) => ({
    ...(exercise.live_id ? exercise : { ...exercise, live_id: createWorkoutExerciseId() }),
    sets: (exercise.sets || []).map((s) => (s._key ? s : { ...s, _key: createSetKey() })),
  }));
}

export function buildReorderIndexMap(previousExercises, nextExercises) {
  const nextIndexById = new Map(
    nextExercises.map((exercise, index) => [exercise.live_id, index])
  );

  return new Map(
    previousExercises
      .map((exercise, index) => [index, nextIndexById.get(exercise.live_id)])
      .filter(([, nextIndex]) => nextIndex !== undefined)
  );
}

export function remapIndexedRecord(record, indexMap) {
  const nextRecord = {};

  Object.entries(record || {}).forEach(([indexKey, value]) => {
    const nextIndex = indexMap.get(Number(indexKey));
    if (nextIndex !== undefined) {
      nextRecord[nextIndex] = value;
    }
  });

  return nextRecord;
}

export function remapCompletedSets(record, indexMap) {
  const nextRecord = {};

  Object.entries(record || {}).forEach(([compoundKey, value]) => {
    const [exerciseIndex, setIndex] = compoundKey.split('-');
    const nextExerciseIndex = indexMap.get(Number(exerciseIndex));
    if (nextExerciseIndex !== undefined) {
      nextRecord[`${nextExerciseIndex}-${setIndex}`] = value;
    }
  });

  return nextRecord;
}

export function remapAppliedProgression(progressionMap, indexMap, workoutSessionKey) {
  const nextMap = new Map();
  const sessionPrefix = `${workoutSessionKey}-`;

  (progressionMap || new Map()).forEach((value, key) => {
    if (!key.startsWith(sessionPrefix)) {
      nextMap.set(key, value);
      return;
    }

    const currentIndex = Number(key.slice(sessionPrefix.length));
    const nextIndex = indexMap.get(currentIndex);
    if (nextIndex !== undefined) {
      nextMap.set(`${sessionPrefix}${nextIndex}`, value);
    }
  });

  return nextMap;
}

export function saveWorkoutToStorage(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}
export function loadWorkoutFromStorage() {
  try { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : null; } catch { return null; }
}
export function clearWorkoutStorage() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/**
 * Normalise a week_schedule value into an ordered list of day_ids.
 * Accepts a string ('rest' | day_id), a list of day_ids, or null/undefined.
 * Drops 'rest'/falsy entries and de-duplicates (first wins).
 */
export function normalizeScheduledDays(value) {
  if (!value) return [];
  const items = Array.isArray(value) ? value : [value];
  const out = [];
  for (const item of items) {
    if (!item || item === 'rest') continue;
    if (!out.includes(item)) out.push(item);
  }
  return out;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** True when a cycle-mode routine is sitting on its end-of-cycle rest debt. */
function cycleNeedsRest(routine) {
  const state = routine.cycle_state || {};
  if (!state._needs_rest || !state.last_completed_at) return false;
  const cycleStart = state.cycle_start_date;
  if (!cycleStart) return false;
  const startMs = Date.parse(cycleStart.includes('T') ? cycleStart : `${cycleStart}T00:00:00Z`);
  if (Number.isNaN(startMs)) return false;
  const totalDays = Math.floor((Date.now() - startMs) / DAY_MS);
  const workoutsDone = state.workouts_in_cycle ?? (routine.cycle_schedule || []).length;
  const restDays = routine.cycle_rest_days ?? 1;
  return totalDays - workoutsDone < restDays;
}

/**
 * Resolve what a single routine schedules for today, honouring `schedule_mode`.
 *
 * MUST stay in lockstep with `_resolve_rotating_workout` / `_resolve_cycle_workout`
 * in backend/routes/dashboard.py — the Dashboard card is rendered from the backend
 * resolution and the Workout tab from this one, so any divergence shows up as the
 * two screens naming different workouts (the v3.8.289 bug: a routine switched to
 * rotating mode kept its stale `week_schedule`, which this function used to read
 * unconditionally).
 *
 * Returns { dayIds, isRest, hasSchedule }.
 */
export function resolveRoutineToday(routine, todayKey) {
  if (!routine) return { dayIds: [], isRest: false, hasSchedule: false };
  const mode = routine.schedule_mode || 'weekly';

  if (mode === 'rotating' || mode === 'cycle') {
    const rotation = routine.cycle_schedule || [];
    if (!rotation.length) return { dayIds: [], isRest: false, hasSchedule: false };

    if (mode === 'rotating') {
      if ((routine.rest_weekdays || []).includes(todayKey)) {
        return { dayIds: [], isRest: true, hasSchedule: true };
      }
      const position = (routine.cycle_state?.position || 0) % rotation.length;
      return { dayIds: [rotation[position]], isRest: false, hasSchedule: true };
    }

    if (cycleNeedsRest(routine)) return { dayIds: [], isRest: true, hasSchedule: true };
    let position = routine.cycle_state?.position || 0;
    if (position >= rotation.length) position = 0;
    return { dayIds: [rotation[position]], isRest: false, hasSchedule: true };
  }

  const slot = routine.week_schedule?.[todayKey];
  return {
    dayIds: normalizeScheduledDays(slot),
    isRest: slot === 'rest',
    hasSchedule: !!routine.week_schedule,
  };
}

/**
 * Returns today's scheduled workouts across coach-assigned and personal routines
 * as an array of candidates. A weekday may hold up to 2 workouts (weekly mode).
 * Coach-assigned routine takes priority; otherwise the first personal routine
 * with a non-rest today wins.
 *
 * Returns:
 *   [{ routine, day, isCoach }]  — one entry per scheduled workout
 *   []                           — rest day or no schedule anywhere
 */
export function getTodayWorkouts(assignedRoutine, personalRoutines) {
  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  const resolve = (routine, isCoach) => {
    const { dayIds, isRest, hasSchedule } = resolveRoutineToday(routine, todayKey);
    const days = dayIds
      .map(id => routine.days?.find(d => d.id === id))
      .filter(Boolean)
      .map(day => ({ routine, day, isCoach }));
    return { days, isRest, hasSchedule };
  };

  // 1. Coach-assigned routine
  if (assignedRoutine) {
    const coach = resolve(assignedRoutine, true);
    if (coach.days.length) return coach.days;
    if (coach.hasSchedule && coach.isRest) return [];
  }

  // 2. Personal routines (first routine with a non-rest today wins)
  for (const routine of personalRoutines || []) {
    const candidate = resolve(routine, false);
    if (!candidate.hasSchedule) continue;
    if (candidate.days.length) return candidate.days;
    if (candidate.isRest) return [];
  }

  return [];
}

/**
 * Back-compat single-result wrapper. Returns the first scheduled workout, or
 * { isRest: true } when today is explicitly a rest day, or null when nothing is set.
 */
export function getTodayWorkout(assignedRoutine, personalRoutines) {
  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const candidates = getTodayWorkouts(assignedRoutine, personalRoutines);
  if (candidates.length) return candidates[0];
  const explicitRest =
    resolveRoutineToday(assignedRoutine, todayKey).isRest ||
    (personalRoutines || []).some(r => resolveRoutineToday(r, todayKey).isRest);
  return explicitRest ? { isRest: true } : null;
}

/**
 * The routine whose schedule pointer drives today's workout, or null when
 * nothing pointer-based is scheduled. Used to decide whether to offer "skip".
 */
export function getSkippableRoutine(assignedRoutine, personalRoutines) {
  const todayKey = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const candidates = getTodayWorkouts(assignedRoutine, personalRoutines);
  const active = candidates[0]?.routine;
  if (!active) return null;
  const mode = active.schedule_mode;
  if (mode !== 'cycle' && mode !== 'rotating') return null;
  if (!resolveRoutineToday(active, todayKey).hasSchedule) return null;
  return { routine: active, isCoach: candidates[0].isCoach };
}
